import {Component, Input, NgZone, OnDestroy, OnInit} from '@angular/core';

interface Step {
  number: string;
  title: string;
  body: string;
}

/**
 * "How it works" on the landing page: four steps, each with a small mock-up of the studio.
 * Steps advance on their own until the visitor hovers, focuses or picks one, and never
 * advance for visitors who prefer reduced motion.
 */
@Component({
  selector: 'app-studio-tour',
  templateUrl: './studio-tour.component.html',
  styleUrls: ['./studio-tour.component.scss']
})
export class StudioTourComponent implements OnInit, OnDestroy {

  private static readonly STEP_INTERVAL_MS = 7000;

  /** Shown in the AI editing menu of the "Write together" preview. */
  @Input() modelName = '';

  readonly steps: Step[] = [
    {
      number: '01',
      title: 'Start anywhere',
      body: 'Begin with a genre and a voice, a plot, or a cast of characters. Blend genres, give writing instructions, or describe a persona for the AI to write as.'
    },
    {
      number: '02',
      title: 'Build your world',
      body: 'Generate detailed character profiles, gather background research (with live web research when facts matter), and shape an outline of chapter summaries and beats.'
    },
    {
      number: '03',
      title: 'Write together',
      body: 'Draft chapters in your style, then select any passage to rewrite it with a prompt, or insert new text at the cursor, with whichever model you prefer.'
    },
    {
      number: '04',
      title: 'Share it',
      body: 'Generate a cover, download your book as a PDF, send a read-only link, or publish it to Discover for other readers to find.'
    }
  ];

  activeStep = 0;
  autoplay = false;

  private stepTimer?: ReturnType<typeof setInterval>;

  constructor(private zone: NgZone) {
  }

  ngOnInit() {
    // Not while prerendering (no window), and never for visitors who prefer reduced motion.
    if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // The timer runs outside Angular so the app still becomes stable, which hydration waits for.
    this.autoplay = true;
    this.zone.runOutsideAngular(() => {
      this.stepTimer = setInterval(() => this.zone.run(() => {
        this.activeStep = (this.activeStep + 1) % this.steps.length;
      }), StudioTourComponent.STEP_INTERVAL_MS);
    });
  }

  ngOnDestroy() {
    this.pauseSteps();
  }

  selectStep(index: number) {
    this.pauseSteps();
    this.activeStep = index;
  }

  /** Once a visitor engages with the steps, stop advancing them on their behalf. */
  pauseSteps() {
    clearInterval(this.stepTimer);
    this.autoplay = false;
  }
}
